import {
    createGlobalNavUrls,
    getActiveNavItemId,
    getGlobalNavTriggerLabel,
    globalNavItemOrder,
    globalNavLabelsSv,
    type GlobalNavSurface,
    type GlobalNavUrls
} from "./navConfig";
import "./global-nav.css";

export type GlobalNavProps = {
    /** Base URL for www (map home + stats), e.g. https://www.centerreach.se or http://localhost:5173 */
    wwwOrigin: string;
    surface: GlobalNavSurface;
    /** When false, Opinion link is omitted */
    showOpinionInGlobalNav: boolean;
    className?: string;
    /** Optional override for individual URLs (merged onto defaults) */
    urlOverrides?: Partial<GlobalNavUrls>;
};

export function GlobalNav({
    wwwOrigin,
    surface,
    showOpinionInGlobalNav,
    className,
    urlOverrides
}: GlobalNavProps): JSX.Element {
    const urls: GlobalNavUrls = { ...createGlobalNavUrls(wwwOrigin), ...urlOverrides };
    const activeId = getActiveNavItemId(surface);
    const portalPrefix = wwwOrigin.replace(/\/$/, "");
    const triggerLabel = getGlobalNavTriggerLabel(surface);
    const isStacked = (className ?? "").includes("stacked");

    const visibleOrder = showOpinionInGlobalNav
        ? globalNavItemOrder
        : globalNavItemOrder.filter((id) => id !== "opinion");

    const externalProps = (href: string): { target?: string; rel?: string } =>
        href.startsWith(portalPrefix) ? {} : { target: "_blank", rel: "noreferrer" };

    const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const el = (e.target as HTMLElement).closest("a, button");
        if (el) {
            // Avoid useRef so the library does not pull a second React copy (peer vs app) in some Vite bundles.
            e.currentTarget.closest("details")?.removeAttribute("open");
        }
    };

    const rootClass = ["cr-global-nav", className].filter(Boolean).join(" ");

    return (
        <div className={rootClass} role="navigation" aria-label="CenterReach">
            <details className="cr-global-nav__dropdown" open={isStacked || undefined}>
                <summary
                    className="cr-global-nav__dropdown-trigger"
                    aria-label={`Meny — du är på ${triggerLabel}`}
                    aria-haspopup="menu"
                >
                    <span className="cr-global-nav__dropdown-trigger-label">{triggerLabel}</span>
                    <span className="cr-global-nav__dropdown-trigger-chevron" aria-hidden />
                </summary>
                <div className="cr-global-nav__dropdown-panel" role="menu" onClick={handlePanelClick}>
                    {visibleOrder.map((id) => {
                        const href = urls[id];
                        const label = globalNavLabelsSv[id];
                        const isActive = activeId === id;
                        return (
                            <a
                                key={id}
                                href={href}
                                role="menuitem"
                                className={`cr-global-nav__link cr-global-nav__dropdown-item${isActive ? " cr-global-nav__link--active" : ""}`}
                                {...externalProps(href)}
                            >
                                {label}
                            </a>
                        );
                    })}
                </div>
            </details>
        </div>
    );
}
